/**
 * Household wallet load-up presets and the minimum load-up floor.
 *
 * The product offers four quick-amount presets — $50 / $100 / $200 /
 * $400 — and a minimum load-up amount that is configurable per school
 * (`Organization.walletMinTopUpCents`, PROD-1317). The minimum is a pure
 * FLOOR: any whole-cents amount at or above it is chargeable, and the
 * presets are the quick-pick subset that clears it.
 *
 * Client UI and the REST validators
 * (`apps/ordo/server/src/routes/billing/{top-up,auto-reload}.ts`) all
 * read from here so the rule can never drift between surfaces. Those two
 * validators are the ONLY ones — the former `@ordo/graphql` twin was
 * deleted with the rest of that package in PROD-1230.
 *
 * Stripe's 50¢ minimum charge (`STRIPE_MIN_CHARGE_CENTS` in
 * `@ordo/stripe`) is intentionally NOT imported — this package stays
 * dependency-free. `MIN_TOP_UP_FLOOR_CENTS` below is what keeps a
 * configured minimum from ever dropping under it.
 */

export const WALLET_TOPUP_PRESETS_CENTS = [5000, 10000, 20000, 40000] as const

/** Largest preset — used to seed/clamp drafts, no longer a validation rule. */
export const MAX_WALLET_PRESET_CENTS = 40000

/**
 * Platform default minimum load-up ($50) — the smallest preset, i.e. the
 * floor that was hardcoded before PROD-1317. Orgs with no override, and
 * households that resolve to no org at all, get exactly this, so turning
 * the feature on changes nothing until someone sets an override.
 */
export const DEFAULT_MIN_TOP_UP_CENTS = 5000

/**
 * Hard floor for a CONFIGURED minimum ($1). Below Stripe's 50¢ per-charge
 * minimum a top-up would fail at the gateway as a 502 rather than a clean
 * 400, and a $0 minimum would let a card be charged for nothing.
 */
export const MIN_TOP_UP_FLOOR_CENTS = 100

/** Hard ceiling for a configured minimum ($1,000) — a typo guard, not a product rule. */
export const MAX_TOP_UP_FLOOR_CENTS = 100_000

export const DEFAULT_AUTO_RELOAD_AMOUNT_CENTS = 10000

/**
 * PROD-1120 — POS register low-balance threshold ($5). Below this, with
 * auto-reload disarmed, the register shows the low-balance alert (and
 * blocks the sale when no menu item is affordable standalone).
 */
export const POS_LOW_BALANCE_WARNING_CENTS = 500

/**
 * The quick-amount buttons to offer under `minCents`: every preset that
 * clears the floor. A minimum above the largest preset leaves none, so the
 * minimum itself becomes the only quick amount — never an empty grid.
 */
export function topUpPresetsForMinimum(minCents: number = DEFAULT_MIN_TOP_UP_CENTS): number[] {
  const offered = WALLET_TOPUP_PRESETS_CENTS.filter((preset) => preset >= minCents)
  return offered.length > 0 ? [...offered] : [minCents]
}

/**
 * Auto-reload amounts are quick-amounts only — no custom values. The
 * runner charges the stored amount verbatim on every pass, so an
 * arbitrary amount must never be storable.
 */
export function isValidAutoReloadAmount(cents: number, minCents: number = DEFAULT_MIN_TOP_UP_CENTS): boolean {
  return topUpPresetsForMinimum(minCents).includes(cents)
}

/**
 * Manual top-ups: any whole-cents amount at or above the minimum.
 *
 * PROD-1317 replaced the old "a preset, or anything above the largest
 * preset" rule with this floor, so a parent may now type any amount their
 * school's minimum allows.
 */
export function isValidTopUpAmount(cents: number, minCents: number = DEFAULT_MIN_TOP_UP_CENTS): boolean {
  if (!Number.isInteger(cents)) {
    return false
  }
  return cents >= minCents
}

/** Smallest offered quick amount that covers `cents`, or null when none does. */
export function smallestPresetAtLeast(cents: number, minCents: number = DEFAULT_MIN_TOP_UP_CENTS): number | null {
  return topUpPresetsForMinimum(minCents).find((preset) => preset >= cents) ?? null
}
